import { useEffect, useState } from 'react';
import { BRAND } from '@/brand';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';
import AdminLayout from '../components/AdminLayout';
import { RefreshCw, Inbox } from 'lucide-react';

/* Admin views for the marketing intake submissions captured by the Grow & Raise /
   Conncct forms: Partner applications, Expert-network applications, Sprint intakes.
   Reads GET /auth/admin/intake; status via PATCH /auth/admin/intake/:type/:id.

   Brand scoping — the endpoint returns everything, this page shows only what
   belongs to the domain it is being viewed on:
   - /partner runs on BOTH brands and stamps each application `program:
     'corporate' | 'consulting'`, so partner rows are filtered to this brand.
     Rows predating that field have no `program` and fall to consulting, the
     default brand, rather than being hidden from everyone.
   - /sprints and /experts are consulting-only (BrandGate in App.tsx), so those
     two tabs are hidden entirely on corporate — there is nothing to show. */

const IS_CORP = BRAND === 'corporate';

type Row = Record<string, any> & { id: string; submittedAt?: string; status?: string; program?: string };
type Intake = { partner: Row[]; expert: Row[]; sprint: Row[] };
type TabKey = keyof Intake;

const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: 'partner', label: 'Partner applications' },
  { key: 'expert', label: 'Expert applications' },
  { key: 'sprint', label: 'Sprint intakes' },
];
const TABS = IS_CORP ? ALL_TABS.filter(t => t.key === 'partner') : ALL_TABS;

// A partner application belongs to this brand's admin view.
const ownPartner = (r: Row) => (String(r.program || '').toLowerCase() === 'corporate') === IS_CORP;

const STATUSES = ['new', 'contacted', 'approved', 'dismissed'];
const HIDE = new Set(['id', 'status', 'submittedAt', 'updatedAt', 'type', 'name', 'firm', 'cname', 'email', 'ref', 'program']);

const fmtDate = (d?: string) => { try { return d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'; } catch { return d || '-'; } };
const label = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase());
const val = (v: any) => Array.isArray(v) ? (v.length ? v.join(', ') : '-') : (v === '' || v == null ? '-' : String(v));

const CSS = `/* Page-specific only — tokens, tabs, buttons and cards come from adminShell.css. */
.adminx .card-h .av{width:40px;height:40px;border-radius:10px;background:var(--brand-soft);color:var(--brand-d);display:grid;place-items:center;font-weight:700;flex:none}.adminx .card-h .em{font-size:12.5px;color:var(--ax-muted)}
.adminx .pill{margin-left:auto;font-size:12px;font-weight:600;border:1px solid var(--ax-line);border-radius:8px;padding:6px 10px;background:#fff;color:#334155;font-family:inherit}
.adminx .kv{display:grid;grid-template-columns:repeat(2,1fr);gap:0 22px;padding:14px 18px}
.adminx .kv div{padding:7px 0;border-bottom:1px solid #F4F6F8;font-size:12.5px}
.adminx .kv span{color:var(--ax-faint);font-weight:600;display:block;font-size:10.5px;text-transform:uppercase;letter-spacing:.04em}
.adminx .kv b{font-weight:600;color:#334155;word-break:break-word}
.adminx .foot{display:flex;align-items:center;gap:10px;padding:10px 18px;border-top:1px solid var(--ax-line-2);font-size:11.5px;color:var(--ax-faint)}
.adminx .intake-empty{background:#fff;border:1px dashed #D7DDE5;border-radius:14px;padding:44px;text-align:center;color:var(--ax-muted)}
.adminx .intake-count{margin:0 0 16px;font-size:12.5px;color:var(--ax-muted);font-weight:600}
@media(max-width:640px){.adminx .kv{grid-template-columns:1fr}}
`;

export default function AdminIntake({ embedded = false, onCount }: { embedded?: boolean; onCount?: (n: number) => void }) {
  const [data, setData] = useState<Intake>({ partner: [], expert: [], sprint: [] });
  const [tab, setTab] = useState<TabKey>('partner');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_BASE_URL}/auth/admin/intake`, { headers: { Authorization: `Bearer ${getToken() || ''}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || 'Failed to load submissions');
      setData({
        partner: (j.intake?.partner || []).filter(ownPartner),
        // Consulting-only intakes — never surfaced on the corporate admin.
        expert: IS_CORP ? [] : (j.intake?.expert || []),
        sprint: IS_CORP ? [] : (j.intake?.sprint || []),
      });
    } catch (e: any) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (type: TabKey, id: string, status: string) => {
    setData(d => ({ ...d, [type]: d[type].map(r => r.id === id ? { ...r, status } : r) })); // optimistic
    try {
      const r = await fetch(`${API_BASE_URL}/auth/admin/intake/${type}/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken() || ''}` }, body: JSON.stringify({ status }) });
      if (!r.ok) throw new Error();
    } catch { load(); } // revert by reloading on failure
  };

  const rows = data[tab];

  /* Everything this brand can see, for the count badge on the Partners hub tab.
     Reported only once loaded, so the badge doesn't flash a 0 on the way in. */
  const total = data.partner.length + data.expert.length + data.sprint.length;
  useEffect(() => { if (!loading && !error) onCount?.(total); }, [total, loading, error, onCount]);

  const title = IS_CORP ? 'Partner applications' : 'Applications & intakes';
  const subtitle = IS_CORP
    ? 'Referral-partner applications submitted on connectcorporate.com. Expert and sprint intakes belong to the consulting brand and appear on conncct.com.'
    : 'Partner applications, expert-network applications and expert-sprint intakes from the marketing site.';
  const refresh = <button className="btn" onClick={load}><RefreshCw /> Refresh</button>;

  /* Content is separated from the page chrome so AdminPartnersHub can render it
     inside its own shell as a tab, without a second sidebar and top bar. */
  const content = (
    <>
      <style>{CSS}</style>

      {/* One tab on corporate (partners only) — a switcher would be pointless.
          Embedded in the Partners hub this is the SECOND level of switching, so
          it renders as filter chips: two identical segmented bars stacked on top
          of each other read as one broken control. The count line is dropped
          there too — the hub tab already carries the number in its badge. */}
      {TABS.length > 1 ? (
        <div className={embedded ? 'chips' : 'tabs'} style={{ marginBottom: 16 }}>
          {TABS.map(t => (
            <button
              key={t.key}
              className={`${embedded ? 'chip' : 'tab'}${tab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}<span className={embedded ? 'ct' : 'cnt'}>{data[t.key].length}</span>
            </button>
          ))}
        </div>
      ) : !embedded && (
        <p className="intake-count">{data.partner.length} application{data.partner.length === 1 ? '' : 's'}</p>
      )}

      {embedded && <div style={{ marginBottom: 16 }}>{refresh}</div>}

      <div className="ax-stack">
      {loading ? <div className="intake-empty">Loading…</div>
        : error ? <div className="intake-empty" style={{ color: '#DC2626', borderColor: '#FCDCDC' }}>{error}</div>
        : rows.length === 0 ? <div className="intake-empty"><b>No {TABS.find(t => t.key === tab)?.label.toLowerCase()} yet</b><p style={{ marginTop: 4 }}>New submissions from the site appear here.</p></div>
        : rows.map(r => {
          const rowTitle = r.firm || r.name || r.cname || r.company || 'Submission';
          const initials = String(rowTitle).trim().split(/\s+/).slice(0, 2).map(x => x[0] || '').join('').toUpperCase() || '?';
          const entries = Object.entries(r).filter(([k, v]) => !HIDE.has(k) && v !== undefined);
          return (
            <div className="card" key={r.id}>
              <div className="card-h">
                <span className="av">{initials}</span>
                <div style={{ minWidth: 0 }}>
                  <b>{rowTitle}</b>
                  <div className="em">{r.email || '-'}{r.name && r.firm ? ` · ${r.name}` : ''}</div>
                </div>
                <select className="pill" value={STATUSES.includes(r.status || '') ? r.status : 'new'} onChange={e => setStatus(tab, r.id, e.target.value)}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {entries.length > 0 && (
                <div className="kv">{entries.map(([k, v]) => <div key={k}><span>{label(k)}</span><b>{val(v)}</b></div>)}</div>
              )}
              <div className="foot">Submitted {fmtDate(r.submittedAt)}{r.ref ? ` · Ref ${r.ref}` : ''}</div>
            </div>
          );
        })}
      </div>
    </>
  );

  if (embedded) return <div className="adminx">{content}</div>;

  return (
    <AdminLayout
      section="Overview"
      title={title}
      icon={Inbox}
      subtitle={subtitle}
      actions={refresh}
    >
      {content}
    </AdminLayout>
  );
}
